/** Profit Centre (private) — costs, profit & margin for training + sales in one place, plus the cash log. */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Home, Lock, GraduationCap, TrendingUp, Wallet, Loader2, PoundSterling, Megaphone, Calendar as CalendarIcon, CalendarClock } from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { StatTile, gbp } from '@/components/training/analytics/shared';
import { MonthlyTable } from '@/components/training/analytics/TurnoverSection';
import { MoneyTab } from '@/components/profit/MoneyTab';
import { WeeklyDigest } from '@/components/profit/WeeklyDigest';
import { SendProfitNowButton } from '@/components/profit/SendProfitNowButton';

const TABS = [
  ['money', 'Money', Wallet], ['training', 'Training', GraduationCap], ['digest', 'Weekly digest', CalendarClock],
];
const iso = (d) => d.toISOString().slice(0, 10);
const startOfMonthsAgo = (n) => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - n); return iso(d); };
const endOfThisMonth = () => { const d = new Date(); return iso(new Date(d.getFullYear(), d.getMonth() + 1, 0)); };

function RangeBar({ start, end, setStart, setEnd, run, busy }) {
  const presets = [
    ['This month', startOfMonthsAgo(0), endOfThisMonth()], ['Last 3 months', startOfMonthsAgo(2), endOfThisMonth()],
    ['Last 6 months', startOfMonthsAgo(5), endOfThisMonth()], ['Last 12 months', startOfMonthsAgo(11), endOfThisMonth()],
    ['This year', `${new Date().getFullYear()}-01-01`, `${new Date().getFullYear()}-12-31`],
  ];
  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[9rem]"><Label className="text-xs">From</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="bg-white" data-testid="pc-start" /></div>
        <div className="flex-1 min-w-[9rem]"><Label className="text-xs">To</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="bg-white" data-testid="pc-end" /></div>
        <Button onClick={run} disabled={busy} className="bg-amber-700 hover:bg-amber-800 text-white font-bold" data-testid="pc-run">{busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CalendarIcon className="w-4 h-4 mr-1.5" />}Run</Button>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">{presets.map(([l, s, e]) => <button key={l} type="button" onClick={() => { setStart(s); setEnd(e); }} className="text-[11px] px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50" data-testid={`pc-preset-${l.toLowerCase().replace(/\s+/g, '-')}`}>{l}</button>)}</div>
    </Card>
  );
}

function TrainingProfit() {
  const [start, setStart] = useState(startOfMonthsAgo(5));
  const [end, setEnd] = useState(endOfThisMonth());
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const run = useCallback(async (quiet = false) => {
    if (!quiet) setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/training-analytics?${new URLSearchParams({ start_date: start, end_date: end, profit: '1' })}`, { credentials: 'include' });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Report failed');
      setData(await res.json());
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }, [start, end]);
  useEffect(() => { run(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const t = data?.totals;
  return (
    <div className="space-y-4" data-testid="pc-training">
      <RangeBar start={start} end={end} setStart={setStart} setEnd={setEnd} run={() => run()} busy={busy} />
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatTile icon={PoundSterling} label="Turnover (billed)" value={gbp(t.billed)} sub={`${gbp(t.billed_ex_vat)} ex VAT · ${t.seats} seats`} tone="good" testid="pc-training-billed" />
            <StatTile icon={Wallet} label="Cash received" value={gbp(t.cash)} sub={t.refunds ? `Net of ${gbp(t.refunds)} refunds` : 'Payments dated in range'} tone="info" testid="pc-training-cash" />
            <StatTile icon={Megaphone} label="Marketing + other costs" value={gbp(t.marketing_cost + t.other_cost)} sub={`+ ${gbp(t.cg_cost)} free C&G certificates`} tone="warn" testid="pc-training-costs" />
            <StatTile icon={TrendingUp} label="Profit" value={`${t.profit < 0 ? '−' : ''}${gbp(Math.abs(t.profit))}`} sub="Ex-VAT turnover − all costs" tone={t.profit >= 0 ? 'good' : 'bad'} testid="pc-training-profit" />
          </div>
          <MonthlyTable data={data} reload={() => run(true)} />
        </>
      )}
    </div>
  );
}

export default function ProfitCentre() {
  const { user, canViewProfit, loading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const rawTab = params.get('tab') || 'money';
  const tab = ['overview', 'sales', 'cash', 'cashin'].includes(rawTab) ? 'money' : rawTab;

  useEffect(() => { if (!loading && user && !canViewProfit) { toast.error("You don't have profit access."); navigate('/staff', { replace: true }); } }, [loading, user, canViewProfit, navigate]);
  if (!user || !canViewProfit) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      <div className="border-b sticky top-0 z-20 shadow-sm bg-[#3b2508] text-white">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3">
          <Link to="/staff" className="text-white/80 hover:text-white" data-testid="pc-back"><ArrowLeft className="w-5 h-5" /></Link>
          <Link to="/" className="text-white/80 hover:text-white hidden sm:inline-flex"><Home className="w-5 h-5" /></Link>
          <div className="w-8 h-8 rounded-lg bg-amber-500/30 flex items-center justify-center"><Lock className="w-4 h-4 text-amber-200" /></div>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-lg font-bold truncate">Profit Centre</h1>
            <p className="hidden sm:block text-[11px] text-white/70">Private — only admins and people given profit access can see this.</p>
          </div>
          <SendProfitNowButton />
        </div>
        <div className="max-w-6xl mx-auto px-3 sm:px-4 flex gap-1 overflow-x-auto">
          {TABS.map(([k, l, Icon]) => (
            <button key={k} type="button" onClick={() => setParams({ tab: k })} data-testid={`pc-tab-${k}`} aria-selected={tab === k}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border-b-2 whitespace-nowrap ${tab === k ? 'border-amber-400 text-white' : 'border-transparent text-white/60 hover:text-white'}`}>
              <Icon className="w-4 h-4" /> {l}
            </button>
          ))}
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 py-6">
        {tab === 'training' && <TrainingProfit />}
        {tab === 'money' && <MoneyTab />}
        {tab === 'digest' && <WeeklyDigest />}
      </div>
    </div>
  );
}
